import React, { useState, useCallback } from "react";
import { Container, Row, Col, Alert, Spinner, Button } from "react-bootstrap";
import { motion, AnimatePresence } from "framer-motion";
import { FaCheckCircle, FaExclamationTriangle } from "react-icons/fa";

import { useKYC } from "../../../hooks/useKYC";

import PersonalInfoAndAddressStep from "./steps/PersonalInfoAndAddressStep";
import EmploymentAndFinancialStep from "./steps/EmploymentAndFinancialStep";
import BusinessStep from "./steps/BusinessStep";
import IdentityVerificationStep from "./steps/IdentityVerificationStep";
import DisclosuresStep from "./steps/DisclosuresStep";
import ReviewStep from "./steps/ReviewStep";

const KYCForm = () => {
  const { loading, submitKYCApplication, clearError } = useKYC();

  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    personalInfo: {},
    address: {},
    employment: {},
    financial: {},
    business: {},
    identity: {},
    disclosures: {},
    general: {},
    riskProfile: "low",
  });
  const [completedSteps, setCompletedSteps] = useState(new Set());
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const steps = [
    {
      id: 1,
      title: "Personal & Address",
      icon: "👤",
      component: PersonalInfoAndAddressStep,
    },
    {
      id: 2,
      title: "Employment & Financial",
      icon: "💼",
      component: EmploymentAndFinancialStep,
    },
    { id: 3, title: "Business Info", icon: "🏢", component: BusinessStep },
    {
      id: 4,
      title: "Identity Verification",
      icon: "📎",
      component: IdentityVerificationStep,
    },
    { id: 5, title: "Disclosures", icon: "📄", component: DisclosuresStep },
    { id: 6, title: "Review & Submit", icon: "🔍", component: ReviewStep },
  ];

  // Update form data (no auto-save)
  const updateFormData = useCallback((section, data) => {
    setFormData((prev) => ({
      ...prev,
      [section]: { ...prev[section], ...data },
    }));
  }, []);

  // Validation functions for each step
  const validateStep = useCallback(
    (stepId, data = formData) => {
      const stepErrors = {};

      switch (stepId) {
        case 1: // Personal & Address
          // Personal Info validation
          if (!data.personalInfo?.fullName?.trim()) {
            stepErrors.fullName = "Full name is required";
          }
          if (!data.personalInfo?.dateOfBirth) {
            stepErrors.dateOfBirth = "Date of birth is required";
          }
          if (!data.personalInfo?.nationality?.trim()) {
            stepErrors.nationality = "Nationality is required";
          }
          if (!data.personalInfo?.email?.trim()) {
            stepErrors.email = "Email is required";
          } else if (
            !/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(
              data.personalInfo.email
            )
          ) {
            stepErrors.email = "Please enter a valid email address";
          }
          if (!data.personalInfo?.phone?.trim()) {
            stepErrors.phone = "Phone number is required";
          }
          if (!data.personalInfo?.idNumber?.trim()) {
            stepErrors.idNumber = "Identification number is required";
          }

          // Address validation
          if (!data.address?.street?.trim()) {
            stepErrors.street = "Street address is required";
          }
          if (!data.address?.city?.trim()) {
            stepErrors.city = "City is required";
          }
          if (!data.address?.state?.trim()) {
            stepErrors.state = "State/Province is required";
          }
          if (!data.address?.postalCode?.trim()) {
            stepErrors.postalCode = "Postal code is required";
          }
          if (!data.address?.country?.trim()) {
            stepErrors.country = "Country is required";
          }
          break;

        case 2: // Employment & Financial
          // Employment validation
          if (!data.employment?.occupation?.trim()) {
            stepErrors.occupation = "Occupation is required";
          }
          if (!data.employment?.employmentStatus) {
            stepErrors.employmentStatus = "Employment status is required";
          }
          if (
            (data.employment?.employmentStatus === "employed" ||
              data.employment?.employmentStatus === "employed-part") &&
            !data.employment?.employerName?.trim()
          ) {
            stepErrors.employerName =
              "Employer name is required for employed individuals";
          }

          // Financial validation
          if (!data.financial?.estimatedInvestment) {
            stepErrors.estimatedInvestment = "Investment amount is required";
          }
          if (!data.financial?.netWorth) {
            stepErrors.netWorth = "Net worth is required";
          }
          if (!data.financial?.monthlyIncome) {
            stepErrors.monthlyIncome = "Monthly income is required";
          }
          if (!data.financial?.sourceOfFunds) {
            stepErrors.sourceOfFunds = "Source of funds is required";
          }
          if (!data.financial?.investmentExperience) {
            stepErrors.investmentExperience =
              "Investment experience is required";
          }
          break;

        case 3: // Business (optional for most users)
          // Business info is typically optional
          break;

        case 4: // Identity Verification
          // Identity verification is handled through file upload,
          // so validation happens in the component itself
          break;

        case 5: // Disclosures
          if (!data.disclosures?.termsAccepted) {
            stepErrors.termsAccepted =
              "You must accept the terms and conditions";
          }
          if (!data.disclosures?.privacyAccepted) {
            stepErrors.privacyAccepted = "You must accept the privacy policy";
          }
          if (!data.disclosures?.riskDisclosureAccepted) {
            stepErrors.riskDisclosureAccepted =
              "You must acknowledge the risk disclosure";
          }
          if (!data.disclosures?.digitalSignature?.trim()) {
            stepErrors.digitalSignature = "Digital signature is required";
          }
          break;

        case 6: // Review - validate all previous steps
          // This will be handled by validating all steps
          break;

        default:
          // No validation for unknown steps
          break;
      }

      return stepErrors;
    },
    [formData]
  );

  // Check if step is valid
  const isStepValid = useCallback(
    (stepId) => {
      const stepErrors = validateStep(stepId);
      return Object.keys(stepErrors).length === 0;
    },
    [validateStep]
  );

  // Handle step completion (with validation)
  const handleStepComplete = useCallback(
    (stepId) => {
      if (isStepValid(stepId)) {
        setCompletedSteps((prev) => new Set([...prev, stepId]));
        // Clear errors for this step
        setErrors((prev) => {
          const newErrors = { ...prev };
          Object.keys(validateStep(stepId)).forEach((key) => {
            delete newErrors[key];
          });
          return newErrors;
        });
      }
    },
    [isStepValid, validateStep]
  );

  // Navigation functions with validation
  const nextStep = useCallback(() => {
    // Validate current step before moving forward
    const stepErrors = validateStep(currentStep);

    if (Object.keys(stepErrors).length > 0) {
      // Show validation errors
      setErrors(stepErrors);

      // Scroll to top to show errors
      window.scrollTo({ top: 0, behavior: "smooth" });

      return;
    }

    // Clear errors and mark step as completed
    setErrors({});
    setCompletedSteps((prev) => new Set([...prev, currentStep]));

    // Move to next step
    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
    }
  }, [currentStep, steps.length, validateStep]);

  const prevStep = useCallback(() => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  }, [currentStep]);

  // Handle document upload (store in form state only)
  const handleDocumentUpload = useCallback(
    (file, documentType) => {
      if (!file) return null;

      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          const fileData = {
            name: file.name,
            type: file.type,
            size: file.size,
            data: e.target.result, // Base64 data
            uploadedAt: new Date().toISOString(),
          };

          // Store in form data
          updateFormData("identity", {
            ...formData.identity,
            documents: {
              ...formData.identity.documents,
              [documentType]: fileData,
            },
          });

          resolve(fileData);
        };
        reader.readAsDataURL(file);
      });
    },
    [formData.identity, updateFormData]
  );

  // Validate all steps before submission
  const validateAllSteps = useCallback(() => {
    const allErrors = {};

    for (let stepId = 1; stepId <= steps.length - 1; stepId++) {
      // Exclude review step
      const stepErrors = validateStep(stepId);
      Object.assign(allErrors, stepErrors);
    }

    return allErrors;
  }, [steps.length, validateStep]);

  // Handle final submission (only database operation)
  const handleSubmit = useCallback(async () => {
    setSubmitError(null);

    // Validate all steps before submission
    const allErrors = validateAllSteps();

    if (Object.keys(allErrors).length > 0) {
      setErrors(allErrors);
      setSubmitError("Please complete all required fields before submitting.");

      // Find first step with errors and go to it
      for (let stepId = 1; stepId <= steps.length - 1; stepId++) {
        const stepErrors = validateStep(stepId);
        if (Object.keys(stepErrors).length > 0) {
          setCurrentStep(stepId);
          window.scrollTo({ top: 0, behavior: "smooth" });
          break;
        }
      }

      return false;
    }

    try {
      // Create the final data payload
      const finalData = {
        ...formData,
        currentStep: steps.length,
        completedSteps: Array.from(completedSteps),
        submittedAt: new Date().toISOString(),
      };

      // Submit using the hook
      const result = await submitKYCApplication(finalData);

      if (!result.success) {
        setSubmitError(result.error);
        return false;
      }

      // Success - move to success screen
      setSubmitted(true);
      setCurrentStep(steps.length + 1);
      return true;
    } catch (err) {
      console.error("Submission failed:", err);
      setSubmitError("Failed to submit application. Please try again.");
      return false;
    }
  }, [
    formData,
    completedSteps,
    steps.length,
    submitKYCApplication,
    validateAllSteps,
    validateStep,
  ]);

  // Calculate progress
  const progress = Math.round((completedSteps.size / steps.length) * 100);

  // Get current step component
  const StepComponent = steps.find(
    (step) => step.id === currentStep
  )?.component;

  return (
    <Container className="pt-12 mt-8">
      {/* Modern flat stepper styles */}
      <style jsx>{`
        .modern-stepper {
          position: relative;
          display: flex;
          align-items: center;
          gap: 0;
          margin: 2rem 0;
        }

        .stepper-line {
          position: absolute;
          top: 18px;
          left: 18px;
          right: 18px;
          height: 2px;
          background: linear-gradient(to right, #e9ecef 0%, #e9ecef 100%);
          z-index: 1;
        }

        .stepper-progress {
          position: absolute;
          top: 0;
          left: 0;
          height: 100%;
          background: linear-gradient(135deg, #00e7d4, #007bff);
          border-radius: 1px;
          transition: width 0.6s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .step-item {
          position: relative;
          z-index: 2;
          display: flex;
          flex-direction: column;
          align-items: center;
          cursor: pointer;
          flex: 1;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .step-circle {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          background: transparent;
          border: 2px solid #e9ecef;
          margin-bottom: 8px;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .step-item.completed .step-circle {
          background: transparent;
          border-color: #28a745;
          color: #28a745;
        }

        .step-item.active .step-circle {
          background: transparent;
          border-color: #007bff;
          color: #007bff;
          transform: scale(1.05);
        }

        .step-item.has-errors .step-circle {
          background: transparent;
          border-color: #ffc107;
          color: #ffc107;
          animation: subtle-pulse 2s infinite;
        }

        .step-icon {
          font-size: 14px;
          line-height: 1;
        }

        .step-title {
          font-size: 11px;
          font-weight: 600;
          color: #6c757d;
          text-align: center;
          line-height: 1.2;
          max-width: 80px;
          transition: color 0.3s ease;
        }

        .step-item.active .step-title {
          color: #007bff;
          font-weight: 700;
        }

        .step-item.completed .step-title {
          color: #28a745;
        }

        .step-item.has-errors .step-title {
          color: #ffc107;
        }

        .step-item:hover .step-circle {
          transform: translateY(-1px);
          border-color: #007bff;
        }

        .step-item.active:hover .step-circle {
          transform: scale(1.05) translateY(-1px);
        }

        .step-item.completed:hover .step-circle {
          border-color: #20c997;
        }

        @keyframes subtle-pulse {
          0% {
            border-color: #ffc107;
          }
          50% {
            border-color: #ff9500;
          }
          100% {
            border-color: #ffc107;
          }
        }

        @media (max-width: 768px) {
          .step-circle {
            width: 32px;
            height: 32px;
          }

          .step-icon {
            font-size: 12px;
          }

          .step-title {
            font-size: 10px;
            max-width: 60px;
          }

          .stepper-line {
            top: 16px;
            left: 16px;
            right: 16px;
          }
        }

        @media (max-width: 576px) {
          .step-title {
            display: none;
          }

          .step-circle {
            width: 28px;
            height: 28px;
          }

          .step-icon {
            font-size: 11px;
          }

          .stepper-line {
            top: 14px;
            left: 14px;
            right: 14px;
          }
        }
      `}</style>
      <div style={{ marginTop: "140px" }}>
        <Row className="justify-content-center">
          <Col lg={8}>
            {/* Header */}
            <div className="pt-12 mt-8">
              <h2 className="mb-3">Know Your Customer (KYC) Application</h2>
              <p className="text-muted mb-4">
                Complete all sections to verify your identity and comply with
                regulatory requirements. Your data will be submitted when you
                complete the final step.
              </p>

              {/* Progress Summary */}
              <div className="text-center mb-2">
                <small className="text-muted">
                  {completedSteps.size} of {steps.length} steps completed (
                  {progress}%)
                </small>
              </div>

              {/* Modern Step Navigation */}
              <div className="modern-stepper mb-4">
                {steps.map((step) => {
                  const hasErrors = step.id <= 5 && !isStepValid(step.id);
                  return (
                    <div
                      key={step.id}
                      className={`step-item ${
                        currentStep === step.id ? "active" : ""
                      } ${completedSteps.has(step.id) ? "completed" : ""} ${
                        hasErrors ? "has-errors" : ""
                      }`}
                      onClick={() => setCurrentStep(step.id)}
                      title={`Go to ${step.title}${
                        hasErrors ? " (has errors)" : ""
                      }`}
                    >
                      <div className="step-circle">
                        <div className="step-icon">
                          {completedSteps.has(step.id) ? (
                            <FaCheckCircle />
                          ) : hasErrors ? (
                            <FaExclamationTriangle />
                          ) : (
                            <span>{step.icon}</span>
                          )}
                        </div>
                      </div>
                      <div className="step-title">{step.title}</div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Error Display */}
            {submitError && (
              <Alert
                variant="danger"
                dismissible
                onClose={() => {
                  setSubmitError(null);
                  clearError();
                }}
                className="mb-4"
              >
                <FaExclamationTriangle className="me-2" />
                {submitError}
              </Alert>
            )}

            {/* Validation Errors Display */}
            {Object.keys(errors).length > 0 && (
              <Alert variant="warning" className="mb-4">
                <FaExclamationTriangle className="me-2" />
                <strong>Please fix the following errors:</strong>
                <ul className="mb-0 mt-2">
                  {Object.entries(errors).map(([field, error]) => (
                    <li key={field}>{error}</li>
                  ))}
                </ul>
              </Alert>
            )}

            {/* Step Content */}
            <Row>
              <Col>
                <AnimatePresence mode="wait">
                  <motion.div
                    key={currentStep}
                    initial={{ opacity: 0, x: 50 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -50 }}
                    transition={{ duration: 0.3 }}
                    className="step-content"
                  >
                    {StepComponent && (
                      <StepComponent
                        formData={formData}
                        setFormData={setFormData}
                        updateFormData={updateFormData}
                        onStepComplete={handleStepComplete}
                        onNextStep={nextStep}
                        onPrevStep={prevStep}
                        currentStep={currentStep}
                        totalSteps={steps.length}
                        isStepCompleted={completedSteps.has(currentStep)}
                        onSubmit={handleSubmit}
                        onUploadDocument={handleDocumentUpload}
                        loading={loading}
                        errors={errors}
                        setErrors={setErrors}
                      />
                    )}
                  </motion.div>
                </AnimatePresence>
              </Col>
            </Row>

            {/* Navigation Buttons */}
            {!submitted && currentStep <= steps.length && (
              <Row className="mt-4">
                <Col>
                  <div className="d-flex justify-content-between align-items-center">
                    <Button
                      variant="outline-secondary"
                      onClick={prevStep}
                      disabled={currentStep === 1}
                      className="px-4"
                    >
                      ← Previous
                    </Button>

                    <div className="text-center">
                      <small className="text-muted">
                        Step {currentStep} of {steps.length}
                      </small>
                    </div>

                    {currentStep < steps.length ? (
                      <Button
                        variant="primary"
                        onClick={nextStep}
                        className="px-4"
                      >
                        Next →
                      </Button>
                    ) : (
                      <Button
                        variant="success"
                        onClick={handleSubmit}
                        disabled={loading}
                        className="px-4"
                      >
                        {loading ? (
                          <>
                            <Spinner
                              as="span"
                              animation="border"
                              size="sm"
                              role="status"
                              className="me-2"
                            />
                            Submitting...
                          </>
                        ) : (
                          "Submit Application"
                        )}
                      </Button>
                    )}
                  </div>
                </Col>
              </Row>
            )}

            {/* Success Screen */}
            {submitted && currentStep > steps.length && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="success-screen text-center py-5"
              >
                <FaCheckCircle size={64} className="text-success mb-4" />
                <h3 className="text-success">KYC Application Submitted!</h3>
                <p className="text-muted mb-4">
                  Your application has been successfully submitted for review.
                  You will receive updates via email regarding the status of
                  your application.
                </p>
                <Alert variant="info">
                  <strong>What's next?</strong>
                  <br />
                  • Our compliance team will review your application
                  <br />
                  • Verification typically takes 1-3 business days
                  <br />• You'll receive email notifications for any updates
                </Alert>
              </motion.div>
            )}
          </Col>
        </Row>
      </div>
    </Container>
  );
};

export default KYCForm;
